import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Link, Alert } from "@mui/material";

import { useAuth } from "../../../contexts/AuthContext";

import logo from "../../../assets/logos/3x-logo.png";

import Form from "../../Form/Form";
import Input from "../../Form/Input/Input";
import RoleAutocompleteInput from "../../Form/Input/RoleAutocompleteInput";
import DefaultButton from "../../../components/simple/DefaultButton";
import PageTitle from "../../PageTitle/PageTitle";

import LinearProgress from "@mui/material/LinearProgress";

import { SignUpContainer } from "./style";

const SignUpFreeTrial = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const programInterest = searchParams.get("programInterest");
  const { saveAuthToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSuccess = (data: any) => {
    if (data?.token) {
      saveAuthToken(data.token);
      const redirectUrl = programInterest
        ? `/dashboard/skills-academy/${programInterest}`
        : "/dashboard/skills-academy";
      window.location.href = redirectUrl;
    } else {
      const redirectUrl = `/login?signup=1&programInterest=${programInterest ?? ""}`;
      window.location.href = redirectUrl;
    }
  };

  const handleError = (err) => {
    console.log(err);
    setErrorMessage(err);
    setLoading(false);
  };

  const SignUpFields = {
    nameFields: [
      {
        label: "First Name",
        id: "first_name",
        type: "text",
        error: "First name is required",
      },
      {
        label: "Last Name",
        id: "last_name",
        type: "text",
        error: "Last name is required",
      },
    ],
    password: [
      {
        label: "Password",
        id: "password",
        type: "password",
        error: "Please enter a password",
      },
      {
        label: "Confirm Password",
        id: "confirm_password",
        type: "password",
        error: "Please confirm your password",
      },
    ],
  };

  return (
    <SignUpContainer>
      <div className="signup-free-trial">
        <img src={logo} alt="GuruNow" />
        <PageTitle
          text="Start your free trial"
        />
        <p>Please fill out your details below</p>
        <Form
          action={`signup`}
          method="POST"
          onSuccess={handleSuccess}
          onBefore={() => setLoading(true)}
          onError={handleError}
          options={{ defaultValues: { token } }}
        >
          {({ register, formState: { errors }, getValues, control, watch }) => {
            const passwordValue = watch("password", "");

            const criteriaList = [
              {
                label: "Password must be at least 8 characters long",
                valid: passwordValue.length >= 8,
              },
              {
                label: "Password must include at least one uppercase letter",
                valid: /[A-Z]/.test(passwordValue),
              },
              {
                label: "Password must include at least one special character",
                valid: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
              },
            ];
            return (
              <>
                <input
                  type="hidden"
                  name="token"
                  value={token}
                  {...register("token")}
                />
                <div className="field-row">
                  {SignUpFields.nameFields.map((item) => (
                    <Input
                      dark
                      key={item.id}
                      id={item.id}
                      showLabel
                      label={item.label}
                      type={item.type}
                      placeHolder={item.label}
                      register={register(item.id, {
                        required: item.error,
                      })}
                      error={errors[item.id]?.message}
                    />
                  ))}
                </div>
                <RoleAutocompleteInput
                  dark
                  id="job_role"
                  label="Job Role"
                  control={control}
                  error={errors.job_role?.message as string | undefined}
                />
                <div className="field-row">
                  {SignUpFields.password.map((item) => {
                    const isConfirm = item.id === "confirm_password";
                    return (
                      <Input
                        dark
                        key={item.id}
                        id={item.id}
                        showLabel
                        label={item.label}
                        type={item.type}
                        placeHolder={item.label}
                        register={register(item.id, {
                          required: item.error,
                          ...(isConfirm
                            ? {
                                validate: (value) => {
                                  const passwordValue = getValues("password");
                                  return (
                                    value === passwordValue ||
                                    "Passwords do not match"
                                  );
                                },
                              }
                            : {
                                minLength: {
                                  value: 8,
                                  message:
                                    "Password must be at least 8 characters long",
                                },
                                validate: {
                                  hasUppercase: (value) =>
                                    /[A-Z]/.test(value) ||
                                    "Password must include at least one uppercase letter",
                                  hasSpecialChar: (value) =>
                                    /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                                    "Password must include at least one special character",
                                },
                              }),
                        })}
                        error={errors[item.id]?.message}
                      />
                    );
                  })}
                </div>
                <div className="password-critiera">
                  {criteriaList.map((item, index) => (
                    <div key={index}>
                      <span className={`icon ${item.valid ? "active" : ""}`}>
                        <svg
                          width="10"
                          height="7"
                          viewBox="0 0 10 7"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 3.5L3.5 6L8.5 1"
                            stroke="#211F45"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      {item.label}
                    </div>
                  ))}
                </div>
                <div className="utils">
                  <div className="checkboxes">
                    <Input
                      id="agree_privacy"
                      showLabel
                      label={
                        <>
                          <span>By signing up you agree to our</span>
                          <Link
                            href="https://www.gurunow.ai/privacy-policy"
                            target="_blank"
                          >
                            Privacy Policy
                          </Link>
                        </>
                      }
                      type="checkbox"
                      register={register("agree_privacy", {
                        required: "Please agree to the privacy policy",
                      })}
                      error={errors["agree_privacy"]?.message}
                    />
                    <Input
                      id="agree_terms"
                      showLabel
                      label={
                        <>
                          <span>By signing up you agree to our</span>
                          <Link href="https://gurunow.ai/terms" target="_blank">
                            Terms & Conditions
                          </Link>
                        </>
                      }
                      type="checkbox"
                      register={register("agree_terms", {
                        required: "Please agree to the terms & conditions",
                      })}
                      error={errors["agree_terms"]?.message}
                    />
                  </div>
                </div>
                {errorMessage && (
                  <>
                    <Alert severity="error" sx={{ mb: 4 }}>
                      {errorMessage}
                    </Alert>
                  </>
                )}
                <DefaultButton type="submit">Get Started</DefaultButton>
                {loading && (
                  <LinearProgress
                    id="login-loading"
                    color="secondary"
                    sx={{
                      mt: 5,
                      height: "8px",
                      borderRadius: "6px",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#962EFF",
                        borderRadius: "6px",
                      },
                    }}
                  />
                )}
              </>
            );
          }}
        </Form>
        <div className="register-helper">
          Already have an account?{" "}
          <Link className="register-link" href="/login">
            Login &rarr;
          </Link>
        </div>
      </div>
    </SignUpContainer>
  );
};

export default SignUpFreeTrial;
